import { type FormEvent, useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Title } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    FLAG_REPORT_ISSUE_TYPES,
    getAdminFlagReports,
    type AdminFlagReportExamTaskScope,
    type AdminFlagReportRecord,
    type AdminFlagReportReporterScope,
    type AdminFlagReportSummary,
} from '../admin/adminFlagReportApi';

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

function formatIssueType(value: string): string {
    if (!value) return 'Unknown';
    return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function getReporterPrimary(record: AdminFlagReportRecord): string {
    return record.userFullName || record.userEmail || record.userId || 'Anonymous Reporter';
}

function getReporterSecondary(record: AdminFlagReportRecord): string {
    if (record.userFullName && record.userEmail) {
        return record.userEmail;
    }

    if (record.userId) {
        return `User ID: ${record.userId}`;
    }

    return 'Anonymous submission';
}

function getExamTaskSummary(record: AdminFlagReportRecord): string {
    const parts = [record.examName, record.examTaskType].filter(Boolean);
    if (!parts.length) {
        return record.examTaskId ? `Exam Task ID: ${record.examTaskId}` : 'No linked exam task';
    }

    return parts.join(' / ');
}

function getMessagePreview(message: string): string {
    const trimmed = message.trim();
    if (!trimmed) return 'No report message provided.';
    return trimmed;
}

function SummaryCard({ label, value }: { label: string; value: number }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 170, flex: '1 1 170px' }}>
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
        </Paper>
    );
}

function IssueCountChips({ summary }: { summary: AdminFlagReportSummary | null }) {
    if (!summary?.issueCounts.length) {
        return null;
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Typography variant="subtitle2">Issue Breakdown</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {summary.issueCounts.map((entry) => (
                        <Chip
                            key={entry.issueType}
                            label={`${formatIssueType(entry.issueType)} (${entry.count})`}
                            variant="outlined"
                        />
                    ))}
                </Stack>
            </Stack>
        </Paper>
    );
}

export function FlagReportPage() {
    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const [issueType, setIssueType] = useState('');
    const [reporterScope, setReporterScope] = useState<AdminFlagReportReporterScope>('all');
    const [examTaskScope, setExamTaskScope] = useState<AdminFlagReportExamTaskScope>('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [rows, setRows] = useState<AdminFlagReportRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState<AdminFlagReportSummary | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [settledRequestKey, setSettledRequestKey] = useState('');
    const [selectedReport, setSelectedReport] = useState<AdminFlagReportRecord | null>(null);

    const requestKey = `${page}:${pageSize}:${search}:${issueType}:${reporterScope}:${examTaskScope}:${refreshNonce}`;
    const isLoading = settledRequestKey !== requestKey;

    useEffect(() => {
        let isActive = true;

        async function loadFlagReports() {
            try {
                const result = await getAdminFlagReports({
                    page: page + 1,
                    pageSize,
                    search,
                    issueType,
                    reporterScope,
                    examTaskScope,
                });

                if (!isActive) {
                    return;
                }

                setRows(result.items);
                setTotal(result.totalCount);
                setSummary(result.summary);
                setErrorMessage('');
                setSettledRequestKey(requestKey);
            } catch (error) {
                if (!isActive) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Unable to load flag reports';
                setRows([]);
                setTotal(0);
                setSummary(null);
                setErrorMessage(message);
                setSettledRequestKey(requestKey);
            }
        }

        void loadFlagReports();

        return () => {
            isActive = false;
        };
    }, [examTaskScope, issueType, page, pageSize, refreshNonce, reporterScope, requestKey, search]);

    function handleSearch(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPage(0);
        setErrorMessage('');
        setSearch(searchDraft.trim());
    }

    function handleResetFilters() {
        setSearchDraft('');
        setSearch('');
        setIssueType('');
        setReporterScope('all');
        setExamTaskScope('all');
        setPage(0);
        setErrorMessage('');
    }

    return (
        <Card>
            <Title title="Flag Reports" />
            {isLoading ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="h4">Flag Reports</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review reported issues across the platform, filter by submission source, and inspect the payload captured with each report.
                        </Typography>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack
                            component="form"
                            spacing={2}
                            onSubmit={handleSearch}
                        >
                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                                <TextField
                                    label="Search Flag Reports"
                                    value={searchDraft}
                                    onChange={(event) => setSearchDraft(event.target.value)}
                                    placeholder="Search by report ID, user, exam task, issue type, or message"
                                    fullWidth
                                />
                                <TextField
                                    select
                                    label="Issue Type"
                                    value={issueType}
                                    onChange={(event) => {
                                        setIssueType(event.target.value);
                                        setPage(0);
                                    }}
                                    sx={{ minWidth: { lg: 240 } }}
                                >
                                    <MenuItem value="">All Issue Types</MenuItem>
                                    {FLAG_REPORT_ISSUE_TYPES.map((entry) => (
                                        <MenuItem key={entry} value={entry}>
                                            {formatIssueType(entry)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    label="Reporter"
                                    value={reporterScope}
                                    onChange={(event) => {
                                        setReporterScope(event.target.value as AdminFlagReportReporterScope);
                                        setPage(0);
                                    }}
                                    sx={{ minWidth: { lg: 180 } }}
                                >
                                    <MenuItem value="all">All Reporters</MenuItem>
                                    <MenuItem value="authenticated">Authenticated</MenuItem>
                                    <MenuItem value="anonymous">Anonymous</MenuItem>
                                </TextField>
                                <TextField
                                    select
                                    label="Exam Task"
                                    value={examTaskScope}
                                    onChange={(event) => {
                                        setExamTaskScope(event.target.value as AdminFlagReportExamTaskScope);
                                        setPage(0);
                                    }}
                                    sx={{ minWidth: { lg: 180 } }}
                                >
                                    <MenuItem value="all">All Reports</MenuItem>
                                    <MenuItem value="with">With Exam Task</MenuItem>
                                    <MenuItem value="without">Without Exam Task</MenuItem>
                                </TextField>
                            </Stack>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                                    Search
                                </Button>
                                <Button variant="outlined" onClick={handleResetFilters}>
                                    Reset
                                </Button>
                                <IconButton aria-label="Refresh flag reports" onClick={() => setRefreshNonce((value) => value + 1)}>
                                    <RefreshIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                        <SummaryCard label="Total Reports" value={summary?.totalReports ?? total} />
                        <SummaryCard label="Authenticated" value={summary?.authenticatedReports ?? 0} />
                        <SummaryCard label="Anonymous" value={summary?.anonymousReports ?? 0} />
                        <SummaryCard label="With Exam Task" value={summary?.reportsWithExamTask ?? 0} />
                        <SummaryCard label="Without Exam Task" value={summary?.reportsWithoutExamTask ?? 0} />
                    </Stack>

                    <IssueCountChips summary={summary} />

                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                        {rows.map((record) => (
                            <Card key={record.id} variant="outlined">
                                <CardContent>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                            <Chip label={formatIssueType(record.issueType)} color="warning" variant="outlined" />
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDateTime(record.createdAt)}
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={0.75}>
                                            <Typography variant="subtitle2">{getReporterPrimary(record)}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {getReporterSecondary(record)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {getExamTaskSummary(record)}
                                            </Typography>
                                            {record.examTaskId ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                    Exam Task ID: {record.examTaskId}
                                                </Typography>
                                            ) : null}
                                        </Stack>

                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {getMessagePreview(record.reportMessage)}
                                        </Typography>

                                        <Button
                                            variant="outlined"
                                            startIcon={<VisibilityOutlinedIcon />}
                                            onClick={() => setSelectedReport(record)}
                                        >
                                            View Payload
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}

                        {!rows.length && !isLoading ? (
                            <Card variant="outlined">
                                <CardContent>
                                    <Alert severity="info">No flag reports matched the current filters.</Alert>
                                </CardContent>
                            </Card>
                        ) : null}
                    </Stack>

                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Reported</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Issue Type</TableCell>
                                    <TableCell>Reporter</TableCell>
                                    <TableCell>Exam Task</TableCell>
                                    <TableCell>Report Message</TableCell>
                                    <DesktopActionHeaderCell width={140}>Payload</DesktopActionHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((record) => (
                                    <TableRow key={record.id} hover>
                                        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                                            <Stack spacing={0.75}>
                                                <Typography variant="body2">{formatDateTime(record.createdAt)}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Updated {formatDateTime(record.updatedAt)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Chip label={formatIssueType(record.issueType)} color="warning" variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Stack spacing={0.75}>
                                                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                                                    {getReporterPrimary(record)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                    {getReporterSecondary(record)}
                                                </Typography>
                                                {record.userId && record.userFullName && record.userEmail ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        User ID: {record.userId}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Stack spacing={0.75}>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                    {getExamTaskSummary(record)}
                                                </Typography>
                                                {record.examTaskId ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        ID: {record.examTaskId}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Box
                                                sx={{
                                                    display: '-webkit-box',
                                                    overflow: 'hidden',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 4,
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {getMessagePreview(record.reportMessage)}
                                            </Box>
                                        </TableCell>
                                        <DesktopActionCell width={140} sx={{ verticalAlign: 'top' }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<VisibilityOutlinedIcon />}
                                                onClick={() => setSelectedReport(record)}
                                            >
                                                View
                                            </Button>
                                        </DesktopActionCell>
                                    </TableRow>
                                ))}

                                {!rows.length && !isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Alert severity="info">No flag reports matched the current filters.</Alert>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={(_event, nextPage) => setPage(nextPage)}
                            onRowsPerPageChange={(event) => {
                                setPageSize(Number(event.target.value));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </TableContainer>
                </Stack>
            </CardContent>

            <Dialog open={Boolean(selectedReport)} onClose={() => setSelectedReport(null)} fullWidth maxWidth="md">
                <DialogTitle>Flag Report Payload</DialogTitle>
                <DialogContent dividers>
                    {selectedReport ? (
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 180 }}>
                                    <Typography variant="overline" color="text.secondary">
                                        Issue Type
                                    </Typography>
                                    <Typography variant="body2">{formatIssueType(selectedReport.issueType)}</Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 180 }}>
                                    <Typography variant="overline" color="text.secondary">
                                        Reporter
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {getReporterPrimary(selectedReport)}
                                    </Typography>
                                </Paper>
                                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 180 }}>
                                    <Typography variant="overline" color="text.secondary">
                                        Exam Task
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {getExamTaskSummary(selectedReport)}
                                    </Typography>
                                </Paper>
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Report Message
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {getMessagePreview(selectedReport.reportMessage)}
                                </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', overflowX: 'auto' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Captured Payload
                                </Typography>
                                <Typography
                                    component="pre"
                                    sx={{
                                        m: 0,
                                        fontFamily: 'Consolas, "Courier New", monospace',
                                        fontSize: '0.85rem',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {selectedReport.contextPayloadJson}
                                </Typography>
                            </Paper>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedReport(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}